import { PageTitle as SharedPageTitle } from '../components/layout/PageHeader';
/**
 * Manual Entry Page
 * Allows users to manually input consumption data for electricity, water, and gas
 */

import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useForm, Controller } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import { Card } from '../components/ui/Card';
import { Button } from '../components/ui/Button';
import { Icon } from '../components/ui/Icon';
import { FormField } from '../components/forms/FormField';
import { ServiceTypeSelect } from '../components/forms/ServiceTypeSelect';
import { EntryConfirmationModal } from '../components/forms/EntryConfirmationModal';
import { LoadingSpinner } from '../components/ui/LoadingSpinner';
import { useManualEntryOperations } from '../hooks/useApi';
import { useCurrencyCode } from '../hooks/useCurrency';
import toast from 'react-hot-toast';
import styled from 'styled-components';
import {
  calculateBagKilograms,
  formatBagQuantity,
  formatNumberEs,
  getAvailableUnits,
  getUnit,
} from '../utils/formatters';
import { BAG_SIZE_OPTIONS } from '../constants';
import type { BagSize } from '../types/api';

// Interface for the Yup schema validation (matches backend schema)
interface ManualEntryFormSchema {
  start_date: string;
  end_date: string;
  service_type:
    | 'electricidad'
    | 'agua'
    | 'gas'
    | 'residuos-reciclables'
    | 'residuos-organicos'
    | 'residuos-generales';
  consumption: number;
  consumption_unit: string;
  notes?: string;
  cost_total?: number;
  currency?: string;
  vendor_name?: string;
  invoice_number?: string;
  issued_date?: string;
  supply_id?: string;
  bag_size?: BagSize;
}

// Interface for API call (matches backend schema)
interface ManualEntryApiData {
  start_date: string;
  end_date: string;
  service_type: string;
  consumption: number;
  consumption_unit: string;
  notes?: string | null;
  cost_total?: number | null;
  currency?: string | null;
  vendor_name?: string | null;
  invoice_number?: string | null;
  issued_date?: string | null;
  supply_id?: string | null;
  bag_size?: BagSize;
  bags?: number;
}

// ============================================================================
// Styled Components
// ============================================================================

const PageContainer = styled.div`
  width: 100%;
  min-width: 0;
  max-width: 52rem;
  margin: 0;
  padding: 0;
`;

const PageHeader = styled.div`
  text-align: left;
  margin-bottom: 1.5rem;
`;

const PageTitle = styled(SharedPageTitle)``;

const PageDescription = styled.p`
  margin: 0.5rem 0 0;
  max-width: 65ch;
  font-size: 0.875rem;
  line-height: 1.6;
  color: var(--text-muted);
`;

const FormSection = styled.section`
  margin-bottom: 2rem;
`;

const SectionTitle = styled.h2`
  font-size: 1.5rem;
  font-weight: 600;
  color: var(--text-primary);
  margin-bottom: 1rem;
`;

const DateRangeContainer = styled.div`
  display: grid;
  gap: 1rem;

  @media (min-width: 768px) {
    grid-template-columns: 1fr 1fr;
    gap: 1.5rem;
  }
`;

const ConsumptionFields = styled.div`
  display: grid;
  gap: 1rem;
`;

const UnitToggleGroup = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.75rem;
`;

const UnitToggleOption = styled.label<{ $selected: boolean }>`
  border: 1px solid
    ${(props) => (props.$selected ? 'var(--brand-500)' : 'var(--border)')};
  border-radius: 999px;
  padding: 0.35rem 1rem;
  font-weight: 600;
  color: ${(props) =>
    props.$selected ? 'var(--brand-600)' : 'var(--text-secondary)'};
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  gap: 0.35rem;
  transition:
    border-color 0.2s ease,
    color 0.2s ease;

  input {
    width: 1rem;
    height: 1rem;
    margin-right: 0.5rem;
    accent-color: var(--action-primary);
  }
  &:focus-within {
    outline: 2px solid var(--interactive-focus);
    outline-offset: 3px;
  }
  min-height: 44px;
`;

const BagSizeGrid = styled.div`
  display: grid;
  gap: 1rem;

  @media (min-width: 768px) {
    grid-template-columns: repeat(3, minmax(0, 1fr));
  }
`;

const BagSizeCard = styled.label<{ $selected: boolean }>`
  border: 2px solid
    ${(props) => (props.$selected ? 'var(--brand-400)' : 'var(--border)')};
  border-radius: var(--radius-md);
  padding: 1rem;
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
  cursor: pointer;
  transition:
    border-color 0.2s ease,
    box-shadow 0.2s ease;

  &:hover {
    border-color: var(--brand-300);
  }

  input {
    width: 1rem;
    height: 1rem;
    margin-right: 0.5rem;
    accent-color: var(--action-primary);
  }
  &:focus-within {
    outline: 2px solid var(--interactive-focus);
    outline-offset: 3px;
  }
  min-height: 44px;

  strong {
    color: var(--text-primary);
    font-size: 0.95rem;
  }

  span {
    color: var(--text-muted);
    font-size: 0.85rem;
  }
`;

const BagPreviewCard = styled.div`
  margin-top: 1rem;
  padding: 1rem;
  border-radius: var(--radius-md);
  border: 1px dashed var(--brand-200);
  background: var(--brand-50);
  color: var(--brand-800);
`;

const BagPreviewTitle = styled.p`
  font-weight: 600;
  margin: 0 0 0.25rem 0;
`;

const FormActions = styled.div`
  display: flex;
  gap: 1rem;
  justify-content: center;
  margin-top: 2rem;

  @media (max-width: 768px) {
    flex-direction: column;
  }
`;

const SuccessMessage = styled.div`
  background: var(--success-50, #e8f5e8);
  border: 1px solid var(--success-600, #4caf50);
  border-radius: 8px;
  padding: 1.5rem;
  margin-top: 2rem;
  text-align: center;
`;

const SuccessTitle = styled.h3`
  color: var(--success-600, #4caf50);
  margin-bottom: 0.5rem;
`;

const SuccessText = styled.p`
  color: var(--text-primary);
  margin: 0;
`;

const ErrorText = styled.p`
  color: var(--danger-600);
  font-size: 0.85rem;
  margin-top: 0.35rem;
`;

// ============================================================================
// Validation Schema
// ============================================================================

const manualEntrySchema = yup.object({
  start_date: yup
    .string()
    .required('La fecha de inicio es requerida')
    .matches(/^\d{4}-\d{2}-\d{2}$/, 'Formato de fecha inválido (YYYY-MM-DD)'),
  end_date: yup
    .string()
    .required('La fecha de fin es requerida')
    .matches(/^\d{4}-\d{2}-\d{2}$/, 'Formato de fecha inválido (YYYY-MM-DD)')
    .test(
      'is-after-start',
      'La fecha de fin debe ser posterior a la fecha de inicio',
      function (value) {
        const { start_date } = this.parent;
        if (!start_date || !value) return true;
        return new Date(value) >= new Date(start_date);
      },
    ),
  service_type: yup
    .string()
    .oneOf(
      [
        'electricidad',
        'agua',
        'gas',
        'residuos-reciclables',
        'residuos-organicos',
        'residuos-generales',
      ],
      'El tipo de servicio es requerido',
    )
    .required('El tipo de servicio es requerido'),
  consumption: yup
    .number()
    .typeError('El consumo es requerido')
    .when('consumption_unit', {
      is: (value: string) => value === 'bolsas',
      then: (schema) =>
        schema
          .integer('Las bolsas deben ser un número entero')
          .min(1, 'Debe registrar al menos 1 bolsa')
          .max(10000, 'El número de bolsas es demasiado alto'),
      otherwise: (schema) =>
        schema
          .min(0, 'El consumo debe ser mayor o igual a 0')
          .max(1000000, 'El consumo parece demasiado alto'),
    })
    .required('El consumo es requerido'),
  consumption_unit: yup.string().required(),
  bag_size: yup
    .string<BagSize>()
    .oneOf(['small', 'medium', 'big'], 'Selecciona un tamaño de bolsa válido')
    .when('consumption_unit', {
      is: (value: string) => value === 'bolsas',
      then: (schema) => schema.required('Selecciona un tamaño de bolsa'),
      otherwise: (schema) => schema.optional(),
    }),
  notes: yup
    .string()
    .max(500, 'Las notas no pueden superar los 500 caracteres')
    .optional(),
  cost_total: yup
    .number()
    .min(0, 'El costo debe ser mayor o igual a 0')
    .optional(),
  currency: yup.string().optional(),
  vendor_name: yup.string().optional(),
  invoice_number: yup.string().optional(),
  issued_date: yup.string().optional(),
  supply_id: yup.string().optional(),
});

// ============================================================================
// Component
// ============================================================================

export const ManualEntryPage: React.FC = () => {
  const navigate = useNavigate();
  const { logEntry, isLoading: isMutationLoading } = useManualEntryOperations();
  const userCurrencyCode = useCurrencyCode();
  const [showConfirmation, setShowConfirmation] = useState(false);
  const [submittedData, setSubmittedData] =
    useState<ManualEntryFormSchema | null>(null);
  const [finalSuccess, setFinalSuccess] = useState(false);

  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
    control,
    watch,
    setValue,
  } = useForm<ManualEntryFormSchema>({
    resolver: yupResolver(manualEntrySchema) as any,
    defaultValues: {
      start_date: new Date().toISOString().split('T')[0] || '',
      end_date: new Date().toISOString().split('T')[0] || '',
      service_type: 'electricidad',
      consumption: 0,
      consumption_unit: getUnit('electricidad'), // Dynamic based on default service type
      notes: '',
      cost_total: 0,
      currency: userCurrencyCode, // Auto-populated from user profile
      vendor_name: '',
      invoice_number: '',
      issued_date: '',
      supply_id: '',
      bag_size: 'medium',
    },
  });

  const serviceType = watch('service_type');
  const consumptionUnit = watch('consumption_unit');
  const bagSize = watch('bag_size');
  const consumptionValue = watch('consumption');
  const bagCount =
    typeof consumptionValue === 'number'
      ? consumptionValue
      : Number(consumptionValue || 0);
  const availableUnits = getAvailableUnits(serviceType);
  const isBagUnit = consumptionUnit === 'bolsas';
  const bagKilogramsPreview =
    isBagUnit && bagSize ? calculateBagKilograms(bagCount || 0, bagSize) : null;
  const showBagPreview = Boolean(isBagUnit && bagSize && bagCount > 0);

  // Update consumption_unit when service_type changes
  useEffect(() => {
    const unit = getUnit(serviceType);
    if (unit) {
      setValue('consumption_unit', unit);
    }
  }, [serviceType, setValue]);

  useEffect(() => {
    if (consumptionUnit === 'bolsas' && !bagSize) {
      setValue('bag_size', 'medium');
    }
  }, [consumptionUnit, bagSize, setValue]);

  // Update currency when user's currency changes
  useEffect(() => {
    if (userCurrencyCode) {
      setValue('currency', userCurrencyCode);
    }
  }, [userCurrencyCode, setValue]);

  // Transform form data before submission
  const transformFormData = (
    data: ManualEntryFormSchema,
  ): ManualEntryApiData => {
    const normalizedUnit =
      data.consumption_unit === 'bolsas'
        ? 'bolsas'
        : getUnit(data.service_type);

    const payload: ManualEntryApiData = {
      start_date: data.start_date,
      end_date: data.end_date,
      service_type: data.service_type,
      consumption: data.consumption,
      consumption_unit: normalizedUnit,
    };

    const sanitizeText = (value?: string) => {
      const trimmed = value?.trim();
      return trimmed ? trimmed : undefined;
    };

    const sanitizePositiveNumber = (value?: number) =>
      typeof value === 'number' && value > 0 ? value : undefined;

    const sanitizedNotes = sanitizeText(data.notes);
    if (sanitizedNotes) {
      payload.notes = sanitizedNotes;
    }

    const sanitizedCostTotal = sanitizePositiveNumber(data.cost_total);
    if (sanitizedCostTotal !== undefined) {
      payload.cost_total = sanitizedCostTotal;
    }

    const sanitizedCurrency = sanitizeText(data.currency);
    if (sanitizedCurrency) {
      payload.currency = sanitizedCurrency;
    }

    const sanitizedVendorName = sanitizeText(data.vendor_name);
    if (sanitizedVendorName) {
      payload.vendor_name = sanitizedVendorName;
    }

    const sanitizedInvoiceNumber = sanitizeText(data.invoice_number);
    if (sanitizedInvoiceNumber) {
      payload.invoice_number = sanitizedInvoiceNumber;
    }

    const sanitizedIssuedDate = sanitizeText(data.issued_date);
    if (sanitizedIssuedDate) {
      payload.issued_date = sanitizedIssuedDate;
    }

    const sanitizedSupplyId = sanitizeText(data.supply_id);
    if (sanitizedSupplyId) {
      payload.supply_id = sanitizedSupplyId;
    }

    if (normalizedUnit === 'bolsas' && data.bag_size) {
      payload.bag_size = data.bag_size;
      payload.bags = Number(data.consumption);
    }

    return payload;
  };

  // Handle form submission - only show confirmation modal, don't save yet
  const onSubmit = async (data: ManualEntryFormSchema) => {
    // Store submitted data and show confirmation modal
    setSubmittedData(data);
    setShowConfirmation(true);
  };

  // Handle confirmation modal actions - actually save the data here
  const handleConfirmEntry = async () => {
    if (!submittedData) return;

    try {
      const transformedData = transformFormData(submittedData);
      await logEntry(transformedData as any);

      setShowConfirmation(false);
      setFinalSuccess(true);
      toast.success(
        '¡Datos confirmados! La información se ha registrado correctamente.',
      );

      // Reset form and show final success
      setTimeout(() => {
        reset();
        setSubmittedData(null);
        setFinalSuccess(false);
      }, 3000);
    } catch (error: any) {
      console.error('Error saving confirmed entry:', error);
      toast.error(
        error.response?.data?.detail ||
          'Error al guardar la entrada manual. Inténtalo de nuevo.',
      );
      // Keep modal open on error so user can retry or edit
    }
  };

  const handleEditEntry = () => {
    setShowConfirmation(false);
    // Keep form data intact for editing
  };

  const handleCloseConfirmation = () => {
    setShowConfirmation(false);
  };

  if (isMutationLoading) {
    return (
      <PageContainer>
        <LoadingSpinner message="Guardando entrada manual..." />
      </PageContainer>
    );
  }

  return (
    <PageContainer>
      <PageHeader>
        <PageTitle>
          <Icon name="edit" size="sm" aria-hidden /> Entrada Manual
        </PageTitle>
        <PageDescription>
          Ingresa manualmente los datos de consumo de tu restaurante cuando no
          tengas facturas disponibles
        </PageDescription>
      </PageHeader>

      <Card>
        <form onSubmit={handleSubmit(onSubmit)}>
          {/* Date Range Section */}
          <FormSection>
            <SectionTitle>
              <Icon name="calendar" size="sm" aria-hidden /> Período de Consumo
            </SectionTitle>
            <DateRangeContainer>
              <FormField
                label="Fecha de Inicio"
                name="start_date"
                type="date"
                required
                register={register}
                error={errors.start_date}
                helperText="Fecha cuando comenzó el período de consumo"
              />
              <FormField
                label="Fecha de Fin"
                name="end_date"
                type="date"
                required
                register={register}
                error={errors.end_date}
                helperText="Fecha cuando terminó el período de consumo"
              />
            </DateRangeContainer>
          </FormSection>

          {/* Consumption Values Section */}
          <FormSection>
            <SectionTitle>
              <Icon name="chart" size="sm" aria-hidden /> Valores de Consumo
            </SectionTitle>
            <p style={{ color: 'var(--text-muted)', marginBottom: '1.5rem' }}>
              Ingresa los valores de consumo para el servicio:
            </p>
            <ConsumptionFields>
              <ServiceTypeSelect
                name="service_type"
                control={control}
                label="Tipo de Servicio"
                required
                {...(errors.service_type && { error: errors.service_type })}
                helperText="Selecciona el tipo de servicio"
              />
              {availableUnits.length > 1 && (
                <div>
                  <SectionTitle as="h3">
                    <Icon name="layers" size="sm" aria-hidden /> Unidad de
                    Registro
                  </SectionTitle>
                  <UnitToggleGroup
                    role="radiogroup"
                    aria-label="Unidad de consumo"
                  >
                    {availableUnits.map((unitOption) => {
                      const selected = consumptionUnit === unitOption;
                      const label =
                        unitOption === 'bolsas'
                          ? 'Bolsas'
                          : `Kilogramos (${unitOption})`;
                      return (
                        <UnitToggleOption key={unitOption} $selected={selected}>
                          <input
                            type="radio"
                            value={unitOption}
                            {...register('consumption_unit')}
                          />
                          {label}
                        </UnitToggleOption>
                      );
                    })}
                  </UnitToggleGroup>
                  {errors.consumption_unit && (
                    <ErrorText>{errors.consumption_unit.message}</ErrorText>
                  )}
                </div>
              )}

              {isBagUnit && (
                <div>
                  <SectionTitle as="h3">
                    <Icon name="bag" size="sm" aria-hidden /> Tamaño de bolsa
                  </SectionTitle>
                  <BagSizeGrid>
                    {BAG_SIZE_OPTIONS.map((option) => (
                      <BagSizeCard
                        key={option.value}
                        $selected={bagSize === option.value}
                      >
                        <input
                          type="radio"
                          value={option.value}
                          {...register('bag_size')}
                        />
                        <strong>{option.label}</strong>
                        <span>{option.description}</span>
                      </BagSizeCard>
                    ))}
                  </BagSizeGrid>
                  {errors.bag_size && (
                    <ErrorText>{errors.bag_size.message}</ErrorText>
                  )}
                </div>
              )}

              <Controller
                name="consumption"
                control={control}
                render={({ field, fieldState }) => (
                  <FormField
                    {...field}
                    label={
                      isBagUnit
                        ? 'Cantidad de bolsas'
                        : `Consumo en ${getUnit(serviceType)}`
                    }
                    type="number"
                    step={isBagUnit ? 1 : 0.01}
                    min={isBagUnit ? '1' : '0'}
                    placeholder={isBagUnit ? 'Ej: 3' : 'Ej: 150.5'}
                    required
                    error={fieldState.error}
                    helperText={
                      isBagUnit
                        ? 'Número total de bolsas utilizadas en el período'
                        : 'Valor de consumo para el período'
                    }
                  />
                )}
              />

              {showBagPreview && bagSize && (
                <BagPreviewCard role="status" aria-live="polite">
                  <BagPreviewTitle>Conversión estimada</BagPreviewTitle>
                  <p style={{ margin: 0 }}>
                    {formatBagQuantity(bagCount, bagSize)} ≈{' '}
                    <strong>
                      {formatNumberEs(bagKilogramsPreview ?? 0, 2)} kg
                    </strong>
                  </p>
                </BagPreviewCard>
              )}
            </ConsumptionFields>
          </FormSection>

          {/* Invoice Metadata Section */}
          <FormSection>
            <SectionTitle>
              <Icon name="file" size="sm" aria-hidden /> Información de la
              Factura (Opcional)
            </SectionTitle>
            <ConsumptionFields>
              <Controller
                name="vendor_name"
                control={control}
                render={({ field, fieldState }) => (
                  <FormField
                    {...field}
                    label="Proveedor"
                    placeholder="Nombre del proveedor"
                    error={fieldState.error}
                  />
                )}
              />
              <Controller
                name="invoice_number"
                control={control}
                render={({ field, fieldState }) => (
                  <FormField
                    {...field}
                    label="Número de Factura"
                    placeholder="Ej: FAC-2024-001"
                    error={fieldState.error}
                  />
                )}
              />
              <Controller
                name="issued_date"
                control={control}
                render={({ field, fieldState }) => (
                  <FormField
                    {...field}
                    type="date"
                    label="Fecha de Emisión"
                    error={fieldState.error}
                  />
                )}
              />
              <Controller
                name="supply_id"
                control={control}
                render={({ field, fieldState }) => (
                  <FormField
                    {...field}
                    label="ID de Suministro"
                    placeholder="Identificador del suministro"
                    error={fieldState.error}
                  />
                )}
              />
            </ConsumptionFields>
          </FormSection>

          {/* Financials Section */}
          <FormSection>
            <SectionTitle>
              <Icon name="money" size="sm" aria-hidden /> Costos (Opcional)
            </SectionTitle>
            <ConsumptionFields>
              <Controller
                name="cost_total"
                control={control}
                render={({ field, fieldState }) => (
                  <FormField
                    {...field}
                    type="number"
                    step="0.01"
                    min="0"
                    label="Costo Total"
                    error={fieldState.error}
                  />
                )}
              />
              {/* Currency is now auto-populated from user profile */}
            </ConsumptionFields>
          </FormSection>

          {/* Notes Section */}
          <FormSection>
            <SectionTitle>
              <Icon name="edit" size="sm" aria-hidden /> Notas (Opcional)
            </SectionTitle>
            <FormField
              label="Notas adicionales"
              name="notes"
              placeholder="Ej: Factura de emergencia, lectura estimada, cambio de proveedor..."
              register={register}
              error={errors.notes}
              helperText="Máximo 500 caracteres"
            />
          </FormSection>

          {/* Form Actions */}
          <FormActions>
            <Button
              type="button"
              variant="secondary"
              onClick={() => navigate('/')}
              disabled={isMutationLoading}
            >
              Cancelar
            </Button>
            <Button
              type="submit"
              variant="primary"
              disabled={isMutationLoading}
              loading={isMutationLoading}
            >
              {isMutationLoading ? 'Guardando...' : 'Guardar Entrada'}
            </Button>
          </FormActions>
        </form>
      </Card>

      {/* Final Success Message */}
      {finalSuccess && (
        <SuccessMessage>
          <SuccessTitle>
            <Icon name="success" size="sm" aria-hidden /> ¡Datos confirmados
            exitosamente!
          </SuccessTitle>
          <SuccessText>
            Tu entrada manual ha sido registrada y se actualizará en el
            dashboard. Puedes continuar agregando más entradas o volver al panel
            principal.
          </SuccessText>
        </SuccessMessage>
      )}

      {/* Entry Confirmation Modal */}
      {submittedData && (
        <EntryConfirmationModal
          isVisible={showConfirmation}
          data={submittedData}
          onConfirm={handleConfirmEntry}
          onEdit={handleEditEntry}
          onClose={handleCloseConfirmation}
          isLoading={isMutationLoading}
        />
      )}
    </PageContainer>
  );
};

export default ManualEntryPage;
